import { useEffect, useMemo, useState } from "react";
import { Card } from "./Layout";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { apiError, DAYS, groupLabel, http } from "../lib/api";

const slotText = (s) =>
  `${DAYS[s.day]} ${s.start}-${s.end} · ${s.label || groupLabel(s.stage_group)}${s.active === false ? " (desactivada)" : ""}`;

/** Gestión de la matrícula y el horario de un alumno desde el panel del equipo. */
export const EnrollmentManager = ({ students, onChanged }) => {
  const [search, setSearch] = useState("");
  const [studentId, setStudentId] = useState("");
  const [slots, setSlots] = useState([]);
  const [addSlot, setAddSlot] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    http.get("/slots").then(({ data }) => setSlots(data)).catch(() => setSlots([]));
  }, []);

  const found = useMemo(
    () => students.filter((s) => s.name.toLowerCase().includes(search.trim().toLowerCase())),
    [students, search]);
  const student = students.find((s) => s.id === studentId);

  const run = async (fn, okMsg) => {
    setMsg(""); setError("");
    try { await fn(); setMsg(okMsg); await onChanged(); }
    catch (err) { setError(apiError(err)); }
  };

  const eligible = student
    ? slots.filter((s) => !student.slots.some((x) => x.id === s.id))
    : [];

  return (
    <Card data-testid="enrollment-manager">
      <h2 className="font-head text-2xl font-medium">Matrículas y horarios</h2>
      <p className="mt-2 text-base text-ink-700">
        Busque al alumno y modifique su horario: añadir clase, quitarla o cambiarla de día y hora.
        El cambio se ve al instante en la cuenta de la familia, porque es la misma matrícula.
      </p>

      <div className="mt-5 max-w-md">
        <Label htmlFor="enr-search" className="text-base">Buscar alumno</Label>
        <Input id="enr-search" data-testid="enrollment-search" value={search}
          placeholder="Nombre del alumno" className="mt-2 h-14 text-lg"
          onChange={(e) => { setSearch(e.target.value); setStudentId(""); }} />
      </div>

      {search && !student && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="enrollment-results">
          {found.length === 0 && <p className="text-base text-ink-700">Ningún alumno con ese nombre.</p>}
          {found.slice(0, 12).map((s) => (
            <Button key={s.id} data-testid={`enrollment-pick-${s.id}`} variant="outline"
              className="h-12 text-base" onClick={() => setStudentId(s.id)}>
              {s.name} · {groupLabel(s.stage_group)}
            </Button>
          ))}
        </div>
      )}

      {msg && <p data-testid="enrollment-success" className="mt-4 rounded-xl bg-logo-light p-4 text-base text-logo-text">{msg}</p>}
      {error && <p data-testid="enrollment-error" className="mt-4 rounded-xl bg-red-50 p-4 text-base text-red-700">{error}</p>}

      {student && (
        <div className="mt-6 rounded-xl bg-cream p-5" data-testid={`enrollment-panel-${student.id}`}>
          <h3 className="font-head text-xl font-medium">
            {student.name} · {groupLabel(student.stage_group)}
          </h3>
          <p className="mt-1 text-base text-ink-700">
            {student.weekly_hours} h en grupo grande y {student.gr_weekly_hours} h de Grupo Reducido ·
            cuota del mes {student.monthly_fee} €
          </p>

          <h4 className="mt-5 font-head text-lg font-medium">Clases actuales</h4>
          {student.slots.length === 0 && (
            <p className="mt-2 text-base text-ink-700">Todavía no tiene ninguna clase asignada.</p>
          )}
          <ul className="mt-2 space-y-3">
            {student.slots.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-3 rounded-lg bg-white p-4"
                data-testid={`enrollment-slot-${s.id}`}>
                <span className="text-base font-medium">{slotText(s)}</span>
                <select data-testid={`enrollment-change-${s.id}`} defaultValue=""
                  className="h-12 rounded-lg border border-border bg-white px-2 text-base"
                  onChange={(e) => {
                    const to = e.target.value;
                    e.target.value = "";
                    if (to) {
                      run(() => http.post("/enrollments/change", {
                        student_id: student.id, from_slot_id: s.id, to_slot_id: to,
                      }), "Horario cambiado. La familia ya lo ve actualizado.");
                    }
                  }}>
                  <option value="">Cambiar a…</option>
                  {eligible.filter((e2) => (e2.kind === "gr") === (s.kind === "gr")).map((e2) => (
                    <option key={e2.id} value={e2.id}>{slotText(e2)}</option>
                  ))}
                </select>
                <Button data-testid={`enrollment-remove-${s.id}`} variant="outline" className="h-12 text-sm"
                  onClick={() => run(
                    () => http.delete("/enrollments", { params: { student_id: student.id, slot_id: s.id } }),
                    "Clase retirada de su matrícula.")}>
                  Quitar esta clase
                </Button>
              </li>
            ))}
          </ul>

          <h4 className="mt-6 font-head text-lg font-medium">Añadir una clase</h4>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <select data-testid="enrollment-add-select" value={addSlot}
              className="h-12 min-w-[320px] rounded-lg border border-border bg-white px-2 text-base"
              onChange={(e) => setAddSlot(e.target.value)}>
              <option value="">Elegir franja…</option>
              {eligible.map((s) => <option key={s.id} value={s.id}>{slotText(s)}</option>)}
            </select>
            <Button data-testid="enrollment-add-btn" className="h-12 text-base" disabled={!addSlot}
              onClick={() => run(async () => {
                await http.post("/enrollments", { student_id: student.id, slot_ids: [addSlot] });
                setAddSlot("");
              }, "Clase añadida a su matrícula.")}>
              Añadir a esta clase
            </Button>
          </div>

          <Button data-testid="enrollment-close" variant="outline" className="mt-6 h-12 text-base"
            onClick={() => { setStudentId(""); setSearch(""); }}>
            Buscar otro alumno
          </Button>
        </div>
      )}
    </Card>
  );
};
